import config from 'config'

// The one place Paddle.js is loaded. packs/application.js calls this on every
// page once the app has mounted (Paddle sends customers back to our pages and
// expects the script there), and the checkout buttons call it again before
// opening the checkout. It used to be a blocking script tag in the page, which
// held every public page back until it had downloaded. In-app browsers,
// content blockers, or a flaky network can stop it from loading, which used to
// surface as `ReferenceError: Can't find variable: Paddle` the moment a user
// clicked subscribe, so this rejects loudly (instead of crashing silently) if
// it can't, and a later call retries.

const PADDLE_SRC = 'https://cdn.paddle.com/paddle/paddle.js'

let pending = null

function setupPaddle() {
  const { environment, vendor_id: vendorId } = config.paddle || {}
  if (environment) window.Paddle.Environment.set(environment)
  window.Paddle.Setup({ vendor: Number(vendorId) })
}

// Resolves with the global Paddle once it's loaded and set up. Rejects if the
// script can't be fetched (the caller is expected to surface this to the user).
export default function ensurePaddle() {
  if (window.Paddle) return Promise.resolve(window.Paddle)
  if (pending) return pending

  pending = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = PADDLE_SRC
    script.async = true
    script.onload = () => {
      try {
        if (!window.Paddle) throw new Error('Paddle.js loaded but the Paddle global is missing')
        setupPaddle()
        resolve(window.Paddle)
      } catch (e) {
        pending = null
        reject(e)
      }
    }
    script.onerror = () => {
      pending = null // allow a later retry
      script.remove()
      reject(new Error('Failed to load Paddle.js'))
    }
    document.head.appendChild(script)
  })

  return pending
}
